<template>
  <Teleport to="body">
    <div
      v-if="showBulkComplete"
      class="fixed inset-0 z-[9997] flex items-center justify-center p-3 overflow-hidden overscroll-none"
      @click.stop
    >
      <button
        class="absolute inset-0 bg-black/50"
        aria-label="Fermer"
        @click.stop="closeBulkComplete"
      ></button>
      <form
        class="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        @submit.prevent
        @click.stop
      >
        <div
          v-if="bulkModalLoading"
          class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/90 rounded-xl"
        >
          <i class="fas fa-spinner fa-spin text-2xl text-blue-600"></i>
          <p class="text-sm text-gray-600">Chargement du formulaire…</p>
        </div>
        <div
          v-else-if="savingBulkComplete"
          class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/90 rounded-xl"
        >
          <i class="fas fa-spinner fa-spin text-2xl text-blue-600"></i>
          <p class="text-sm font-medium text-black">
            Application des modifications…
          </p>
          <p v-if="bulkProgressLabel" class="text-sm text-blue-700">
            {{ bulkProgressLabel }}
          </p>
          <p v-else class="text-xs text-gray-500">
            Mise à jour de {{ bulkSelectionProducts.length }} fiche(s), merci
            de patienter.
          </p>
        </div>
        <div class="p-5 border-b border-gray-200 flex justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-black">
              Compléter la sélection
            </h2>
            <p class="text-sm text-gray-600 mt-1">
              {{ bulkSelectionProducts.length }} produit(s) sélectionné(s).
              Seuls les champs renseignés ci-dessous seront appliqués.
              <span
                v-if="bulkBatchCount > 1"
                class="block mt-1 text-xs text-blue-700"
              >
                Traitement automatique par lots de
                {{ SHOPIFY_BULK_BATCH_SIZE }} fiches ({{ bulkBatchCount }}
                lots).
              </span>
            </p>
          </div>
          <button
            type="button"
            class="text-gray-500"
            :disabled="bulkModalLoading || savingBulkComplete"
            @click="closeBulkComplete"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="px-5 py-4 border-b border-gray-200 space-y-3">
          <p class="text-sm font-semibold text-black">Produits concernés</p>
          <div class="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
            <span
              v-for="product in bulkSelectionProducts"
              :key="`bulk-ref-${product.id}`"
              class="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
            >
              {{ product.reference || product.id }}
            </span>
          </div>
          <div v-if="bulkMissingSummary.length" class="text-sm text-gray-600">
            <p class="font-medium text-black mb-1">Champs encore manquants</p>
            <ul class="space-y-1">
              <li v-for="[field, count] in bulkMissingSummary" :key="field">
                {{ field }} : {{ count }}/{{ bulkSelectionProducts.length }}
              </li>
            </ul>
          </div>
        </div>

        <div class="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="md:col-span-2 space-y-3">
            <p class="text-sm font-semibold text-black">Catégorie eFashion</p>
            <CategoryTreeAutocomplete
              :tree="categoriesTree"
              :cat-id="bulkSelectedCategoryLevel1"
              :sous-id="bulkSelectedCategoryLevel2"
              :sous-sous-id="bulkSelectedCategoryLevel3"
              label="Recherche rapide catégorie"
              placeholder="Rechercher une catégorie (ex. robe, jean…)"
              @select="onBulkCategoryPathSelect"
              @clear="onBulkCategoryPathClear"
            />
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              <BaseInput
                v-model="bulkSelectedCategoryLevel1"
                type="select"
                open-on-focus
                label="Catégorie"
                :options="categoryLevel1Options"
                placeholder="Ne pas modifier"
                @change="onBulkCategoryLevel1Change"
              />
              <BaseInput
                v-model="bulkSelectedCategoryLevel2"
                type="select"
                open-on-focus
                label="Sous-catégorie"
                :options="bulkCategoryLevel2Options"
                placeholder="Ne pas modifier"
                :disabled="!bulkSelectedCategoryLevel1"
                @change="onBulkCategoryLevel2Change"
              />
              <BaseInput
                v-model="bulkSelectedCategoryLevel3"
                type="select"
                open-on-focus
                label="Sous-sous-catégorie"
                :options="bulkCategoryLevel3Options"
                placeholder="Ne pas modifier"
                :disabled="
                  !bulkSelectedCategoryLevel2 ||
                  !bulkCategoryLevel3Options.length
                "
                @change="onBulkCategoryLevel3Change"
              />
            </div>
          </div>
          <BaseInput
            v-model="bulkForm.brandId"
            type="select"
            open-on-focus
            label="Marque"
            :options="brandOptions"
            placeholder="Ne pas modifier"
          />
          <BaseInput
            v-model="bulkForm.collectionId"
            type="select"
            open-on-focus
            label="Collection"
            :options="collectionOptions"
            placeholder="Ne pas modifier"
          />
          <BaseInput
            v-model="bulkForm.provenanceId"
            type="select"
            open-on-focus
            label="Provenance"
            :options="provenanceOptions"
            placeholder="Ne pas modifier"
          />
            <div>
              <BaseInput
                v-model="bulkForm.price"
                type="number"
                label="Prix HT"
                step="0.01"
                min="0"
                placeholder="Ne pas modifier"
              />
              <p class="text-xs text-amber-700 mt-1">
                Les prix Shopify sont souvent des prix B2C. Indiquez le prix
                grossiste HT eFashion si besoin.
              </p>
            </div>
            <div>
              <BaseInput
                v-model="bulkForm.weight"
                type="number"
                label="Poids unitaire (kg)"
                step="0.001"
                min="0.001"
                :max="5"
                placeholder="Ne pas modifier"
              />
              <p class="text-xs text-red-600 mt-0.5">
                Merci de bien vouloir augmenter légèrement le poids pour prendre
                en compte les cintres, cartons ou surplus d'emballage. Maximum
                5 kg.
              </p>
            </div>
          <div class="md:col-span-2">
            <CompositionForm
              v-model="bulkSelectedCompositions"
              :composition-type="bulkCompositionType"
              :localisations="bulkCompositionLocations"
              :compositions-list="bulkShoeCompositions"
              :autocomplete-options="bulkCompositionAutocompleteOptions"
              :show-autocomplete-dropdown="bulkShowCompositionDropdown"
              :is-loading-autocomplete="bulkIsLoadingCompositionAutocomplete"
              @composition-input="handleBulkCompositionInput"
              @hide-dropdown="hideBulkCompositionDropdown"
              @select-autocomplete="selectBulkComposition"
            />
          </div>
          <BaseInput
            v-model="bulkForm.soldBy"
            type="select"
            open-on-focus
            label="Vendu par"
            :options="soldByOptions"
            placeholder="Ne pas modifier"
          />
          <p class="md:col-span-2 -mt-2 text-xs text-gray-500">
            À choisir manuellement : Shopify ne fournit pas le mode de vente
            eFashion.
          </p>
          <BaseInput
            v-model="bulkForm.sizeRangeId"
            type="select"
            open-on-focus
            label="Taille du paquet"
            :options="sizeRangeOptions"
            placeholder="Ne pas modifier"
          />
          <BaseInput
            v-if="bulkForm.soldBy && bulkForm.soldBy !== 'tailles'"
            v-model="bulkForm.packId"
            type="select"
            open-on-focus
            label="Quantité du paquet"
            :options="bulkPackOptions"
            placeholder="Ne pas modifier"
          />
          <div class="md:col-span-2">
            <CouleursSelector
              v-model="bulkSelectedColors"
              :couleurs-list="officialColors"
              :tailles-list="
                bulkForm.soldBy === 'melangees'
                  ? bulkSelectedSizeNames
                  : undefined
              "
              :force-validate="Boolean(bulkFormValidationMessage)"
            />
            <p class="mt-2 text-xs text-gray-500">
              Optionnel : laissez vide pour ne pas modifier les couleurs des
              produits sélectionnés.
            </p>
            <div
              v-if="bulkForm.soldBy === 'melangees'"
              class="mt-2 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r"
            >
              <p v-if="!bulkForm.sizeRangeId" class="text-sm text-blue-700">
                Sélectionnez la déclinaison pour saisir les quantités par
                taille.
              </p>
              <p
                v-else-if="bulkMelangeesPackMismatchMessage"
                class="text-sm text-red-600"
              >
                {{ bulkMelangeesPackMismatchMessage }}
              </p>
              <p v-else class="text-xs text-blue-600">
                Pour chaque couleur, indiquez la composition du paquet par
                taille (obligatoire, au moins une quantité &gt; 0 par
                couleur). La somme doit correspondre au pack sélectionné.
              </p>
            </div>
          </div>
        </div>

        <div class="p-5 border-t border-gray-200 space-y-3">
          <p v-if="bulkFormValidationMessage" class="text-sm text-red-600">
            {{ bulkFormValidationMessage }}
          </p>
          <div class="flex justify-end gap-2">
            <ActionButton
              label="Annuler"
              variant="secondary"
              type="button"
              :disabled="bulkModalLoading || savingBulkComplete"
              @click="closeBulkComplete"
            />
            <ActionButton
              label="Appliquer à la sélection"
              variant="primary"
              type="button"
              :loading="savingBulkComplete"
              :disabled="
                bulkModalLoading || savingBulkComplete || !canSaveBulkForm
              "
              @click="saveBulkComplete"
            />
          </div>
        </div>
      </form>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { inject } from "vue";
import BaseInput from "@/components/forms/BaseInput.vue";
import CategoryTreeAutocomplete from "@/components/forms/CategoryTreeAutocomplete.vue";
import CouleursSelector from "@/components/mel/CouleursSelector.vue";
import CompositionForm from "@/components/mel/CompositionForm.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import {
  useShopifyMelBulkSheet,
  type ShopifyMelBulkSheetContext,
} from "@/composables/useShopifyMelBulkSheet";

const context = inject<ShopifyMelBulkSheetContext>("shopifyMelBulkContext");
if (!context) {
  throw new Error("shopifyMelBulkContext is required");
}

const {
  SHOPIFY_BULK_BATCH_SIZE,
  showBulkComplete,
  bulkModalLoading,
  savingBulkComplete,
  bulkProgressLabel,
  bulkSelectionProducts,
  bulkBatchCount,
  bulkMissingSummary,
  bulkForm,
  bulkSelectedCategoryLevel1,
  bulkSelectedCategoryLevel2,
  bulkSelectedCategoryLevel3,
  bulkSelectedCompositions,
  bulkCompositionType,
  bulkCompositionLocations,
  bulkShoeCompositions,
  bulkCompositionAutocompleteOptions,
  bulkShowCompositionDropdown,
  bulkIsLoadingCompositionAutocomplete,
  bulkSelectedColors,
  bulkFormValidationMessage,
  canSaveBulkForm,
  bulkMelangeesPackMismatchMessage,
  brandOptions,
  collectionOptions,
  provenanceOptions,
  sizeRangeOptions,
  categoryLevel1Options,
  bulkCategoryLevel2Options,
  bulkCategoryLevel3Options,
  bulkPackOptions,
  bulkSelectedSizeNames,
  soldByOptions,
  categoriesTree,
  officialColors,
  openBulkComplete,
  closeBulkComplete,
  saveBulkComplete,
  onBulkCategoryPathSelect,
  onBulkCategoryLevel1Change,
  onBulkCategoryLevel2Change,
  onBulkCategoryLevel3Change,
  onBulkCategoryPathClear,
  handleBulkCompositionInput,
  selectBulkComposition,
  hideBulkCompositionDropdown,
} = useShopifyMelBulkSheet(context);

defineExpose({ openBulkComplete });
</script>
